import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { publicOnboardingApi, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Car, IdentificationCard, Camera, CheckCircle, Warning } from '@phosphor-icons/react';
import { toast } from 'sonner';

const RegisterStart = () => {
  const navigate = useNavigate();
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      const res = await publicOnboardingApi.startPublic();
      navigate(`/onboarding/${res.data.token}`, { replace: true });
    } catch (e) {
      const msg = formatApiError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setStarting(false);
    }
  };

  // Auto-start bei Direktaufruf per QR-Code
  useEffect(() => {
    const search = new URLSearchParams(window.location.search);
    if (search.get('auto') === '1') {
      start();
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 mb-3">
            <div className="w-12 h-12 rounded-sm bg-[#E60000] flex items-center justify-center">
              <Car weight="bold" size={26} className="text-white" />
            </div>
            <span className="text-xl font-bold text-[#111418]">AutoVermietung</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#111418]">
            Als Kunde registrieren
          </h1>
          <p className="text-sm text-[#525866] mt-2 max-w-md mx-auto">
            Bequem online. Sie tragen Ihre Daten ein und laden Ausweis + Führerschein hoch —
            wir prüfen alles vorab, damit die Fahrzeugübernahme in wenigen Minuten erledigt ist.
          </p>
        </div>

        <div className="card-swiss p-6 mb-4" data-testid="register-start-panel">
          <h2 className="font-bold text-[#111418] mb-3">Das brauchen Sie</h2>
          <ul className="space-y-2 text-sm">
            <li className="flex items-start gap-2">
              <CheckCircle size={16} weight="bold" className="text-[#009951] mt-0.5" />
              <span>Ihre persönlichen Daten und Adresse</span>
            </li>
            <li className="flex items-start gap-2">
              <IdentificationCard size={16} weight="bold" className="text-[#009951] mt-0.5" />
              <span>Personalausweis (Vorder- und Rückseite)</span>
            </li>
            <li className="flex items-start gap-2">
              <IdentificationCard size={16} weight="bold" className="text-[#009951] mt-0.5" />
              <span>Führerschein (Vorder- und Rückseite)</span>
            </li>
            <li className="flex items-start gap-2">
              <Camera size={16} weight="bold" className="text-[#009951] mt-0.5" />
              <span>Kamera oder gescannte Fotos — geht direkt vom Handy</span>
            </li>
          </ul>

          <div className="mt-6 p-3 bg-[#F7F8FA] border border-[#E5E7EB] rounded-sm text-xs text-[#525866]">
            Dauer: ca. 3 Minuten · Ihre Daten werden verschlüsselt übertragen und nur intern verarbeitet.
          </div>

          <Button
            className="btn-primary w-full mt-6"
            onClick={start}
            disabled={starting}
            data-testid="start-registration-btn"
          >
            {starting ? 'Wird gestartet…' : 'Registrierung starten'}
          </Button>
        </div>

        {error && (
          <div className="card-swiss p-4 border-l-4 border-l-[#E60000]" data-testid="register-error">
            <p className="text-sm text-[#E60000] flex items-start gap-2">
              <Warning size={16} weight="bold" className="mt-0.5" />
              {error}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RegisterStart;
